package com.havn.app.features.agency.data

import com.havn.app.core.logging.HavnLogger
import com.havn.app.core.media.HavnMediaBucket
import com.havn.app.core.media.HavnPickedPhoto
import com.havn.app.core.media.havnFileExtension
import com.havn.app.core.media.havnPublicStorageSource
import com.havn.app.features.agency.domain.Agency
import com.havn.app.features.agency.domain.AgencyInvitation
import com.havn.app.features.agency.domain.AgencyMember
import com.havn.app.features.agency.domain.AgencyMemberStatus
import com.havn.app.features.agency.domain.AgencyRepository
import com.havn.app.features.agency.domain.AgencyRole
import com.havn.app.features.agency.domain.HavnSubscriptionTier
import com.havn.app.features.agency.domain.UserEntitlement
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.postgrest.from
import io.github.jan.supabase.postgrest.postgrest
import io.github.jan.supabase.postgrest.rpc
import io.github.jan.supabase.postgrest.query.Columns
import io.github.jan.supabase.postgrest.query.Order
import io.github.jan.supabase.storage.storage
import io.ktor.http.ContentType
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

class SupabaseAgencyRepository(
    private val client: SupabaseClient
) : AgencyRepository {
    override suspend fun entitlementForUser(userId: String): Result<UserEntitlement> = runCatching {
        client.from(USER_ENTITLEMENTS)
            .select(UserEntitlementColumns) {
                filter { eq("user_id", userId) }
                limit(1)
            }
            .decodeSingleOrNull<UserEntitlementRow>()
            ?.toDomain()
            ?: UserEntitlement()
    }.onFailure {
        HavnLogger.error("Agency", "Failed to load entitlement for $userId", it)
    }

    override suspend fun agenciesForUser(userId: String): Result<List<Agency>> = runCatching {
        val memberships = client.from(AGENCY_MEMBERS)
            .select {
                filter {
                    eq("agent_id", userId)
                    eq("status", "active")
                }
            }
            .decodeList<AgencyMemberRow>()

        if (memberships.isEmpty()) return@runCatching emptyList()

        val agenciesById = client.from(AGENCIES)
            .select(AgencyColumns) {
                filter { isIn("id", memberships.map { it.agencyId }) }
                order("created_at", Order.DESCENDING)
            }
            .decodeList<AgencyRow>()
            .associateBy { it.id }

        memberships.mapNotNull { membership ->
            agenciesById[membership.agencyId]?.toDomain(
                role = AgencyRole.fromDatabase(membership.role),
                memberCount = memberCount(membership.agencyId)
            )
        }
    }.onFailure {
        HavnLogger.error("Agency", "Failed to load agencies for $userId", it)
    }

    override suspend fun membersForAgency(agencyId: String): Result<List<AgencyMember>> = runCatching {
        val memberships = client.from(AGENCY_MEMBERS)
            .select(AgencyMemberDetailColumns) {
                filter { eq("agency_id", agencyId) }
                order("created_at", Order.ASCENDING)
            }
            .decodeList<AgencyMemberDetailRow>()

        val agentsById = memberships
            .map { it.agentId }
            .takeIf { it.isNotEmpty() }
            ?.let { agentIds ->
                client.from(AGENTS)
                    .select(AgentColumns) {
                        filter { isIn("id", agentIds) }
                    }
                    .decodeList<AgentRow>()
                    .associateBy { it.id }
            }
            ?: emptyMap()

        val activeMembers = memberships.map { membership ->
            val agent = agentsById[membership.agentId]
            AgencyMember(
                agentId = membership.agentId,
                fullName = agent?.fullName ?: agent?.email ?: "Agency member",
                email = agent?.email.orEmpty(),
                avatarUrl = agent?.displayAvatarUrl,
                role = AgencyRole.fromDatabase(membership.role),
                status = AgencyMemberStatus.fromDatabase(membership.status)
            )
        }

        val invitations = client.from(AGENCY_INVITATIONS)
            .select(AgencyInvitationColumns) {
                filter {
                    eq("agency_id", agencyId)
                    eq("status", "pending")
                }
                order("created_at", Order.DESCENDING)
            }
            .decodeList<AgencyInvitationRow>()
            .map { invitation ->
                AgencyMember(
                    agentId = null,
                    invitationId = invitation.id,
                    fullName = invitation.email,
                    email = invitation.email,
                    role = AgencyRole.fromDatabase(invitation.role),
                    status = AgencyMemberStatus.Invited
                )
            }

        activeMembers + invitations
    }.onFailure {
        HavnLogger.error("Agency", "Failed to load members for $agencyId", it)
    }

    override suspend fun pendingInvitationsForCurrentUser(): Result<List<AgencyInvitation>> = runCatching {
        client.postgrest.rpc("pending_agency_invitations_for_current_user")
            .decodeList<PendingAgencyInvitationRow>()
            .map { it.toDomain() }
    }.onFailure {
        HavnLogger.error("Agency", "Failed to load pending agency invitations", it)
    }

    override suspend fun createAgency(userId: String, name: String): Result<Agency> = runCatching {
        val normalizedName = name.trim()
        require(normalizedName.length >= 2) { "Agency name is required." }

        val agency = client.from(AGENCIES)
            .insert(AgencyInsertRow(name = normalizedName, createdByAgentId = userId)) {
                select()
            }
            .decodeSingle<AgencyRow>()

        client.from(AGENCY_MEMBERS).insert(
            AgencyMemberInsertRow(
                agencyId = agency.id,
                agentId = userId,
                role = AgencyRole.Owner.databaseValue,
                status = "active",
                invitedByAgentId = userId
            )
        )

        val workspace = client.from(WORKSPACES)
            .insert(
                WorkspaceInsertRow(
                    type = "agency",
                    agencyId = agency.id,
                    name = normalizedName
                )
            ) {
                select()
            }
            .decodeSingle<WorkspaceRow>()

        client.from(AGENCIES)
            .update(mapOf("workspace_id" to workspace.id)) {
                filter { eq("id", agency.id) }
            }

        client.postgrest.rpc(
            MIGRATE_PERSONAL_DATA_TO_AGENCY_RPC,
            MigratePersonalDataToAgencyParams(agencyId = agency.id)
        )

        agency.copy(workspaceId = workspace.id).toDomain(role = AgencyRole.Owner, memberCount = 1)
    }.onFailure {
        HavnLogger.error("Agency", "Failed to create agency '${name.trim()}'", it)
    }

    override suspend fun updateAgencyBranding(
        agencyId: String,
        name: String,
        iconUrl: String?,
        logoPhoto: HavnPickedPhoto?
    ): Result<Agency> = runCatching {
        val normalizedName = name.trim()
        require(normalizedName.length >= 2) { "Agency name is required." }
        val nextIconPath = logoPhoto?.let { photo ->
            require(photo.bytes.isNotEmpty()) { "Choose a non-empty agency logo." }
            val contentType = photo.contentType.ifBlank { "image/jpeg" }
            val safeName = photo.fileName.sanitizeFileName()
            val storagePath = "$agencyId/logo/${timestampSlug()}-$safeName".withImageExtension(contentType)
            client.storage.from(HavnMediaBucket.AgencyMedia).upload(storagePath, photo.bytes) {
                upsert = true
                this.contentType = ContentType.parse(contentType)
            }
            storagePath
        } ?: iconUrl

        val updatedAgency = client.from(AGENCIES)
            .update(
                AgencyBrandingUpdateRow(
                    name = normalizedName,
                    iconUrl = nextIconPath,
                    iconStoragePath = nextIconPath
                )
            ) {
                filter { eq("id", agencyId) }
                select()
            }
            .decodeSingle<AgencyRow>()

        updatedAgency.workspaceId?.let { workspaceId ->
            client.from(WORKSPACES).update(WorkspaceNameUpdateRow(name = normalizedName)) {
                filter { eq("id", workspaceId) }
            }
        }

        updatedAgency.toDomain(
            role = AgencyRole.fromDatabase(
                client.from(AGENCY_MEMBERS)
                    .select(Columns.list("role")) {
                        filter {
                            eq("agency_id", agencyId)
                            eq("status", "active")
                        }
                        limit(1)
                    }
                    .decodeSingleOrNull<AgencyRoleOnlyRow>()
                    ?.role
            ),
            memberCount = memberCount(agencyId)
        )
    }.onFailure {
        HavnLogger.error("Agency", "Failed to update agency branding for $agencyId", it)
    }

    override suspend fun inviteAgent(
        agencyId: String,
        invitedByAgentId: String,
        email: String,
        role: AgencyRole
    ): Result<Unit> = runCatching {
        val normalizedEmail = email.trim().lowercase()
        require(normalizedEmail.contains("@")) { "Enter a valid agent email." }
        require(role != AgencyRole.Owner) { "Owner role cannot be invited directly." }

        client.postgrest.rpc(
            function = "invite_existing_agency_agent",
            parameters = InviteExistingAgencyAgentParams(
                agencyId = agencyId,
                email = normalizedEmail,
                role = role.databaseValue
            )
        )
        Unit
    }.onFailure {
        HavnLogger.error("Agency", "Failed to invite $email to agency $agencyId", it)
    }

    override suspend fun deleteInvite(invitationId: String): Result<Unit> = runCatching {
        client.postgrest.rpc(
            function = "delete_agency_invitation",
            parameters = DeleteAgencyInvitationParams(invitationId = invitationId)
        )
        Unit
    }.onFailure {
        HavnLogger.error("Agency", "Failed to delete invite $invitationId", it)
    }

    override suspend fun updateMemberRole(
        agencyId: String,
        agentId: String,
        role: AgencyRole
    ): Result<Unit> = runCatching {
        require(role != AgencyRole.Owner) { "Owner role cannot be assigned here." }
        client.postgrest.rpc(
            function = "update_agency_member_role",
            parameters = UpdateAgencyMemberRoleParams(
                agencyId = agencyId,
                agentId = agentId,
                role = role.databaseValue
            )
        )
        Unit
    }.onFailure {
        HavnLogger.error("Agency", "Failed to update member $agentId role in $agencyId", it)
    }

    override suspend fun removeMemberAccess(
        agencyId: String,
        agentId: String
    ): Result<Unit> = runCatching {
        client.postgrest.rpc(
            function = "remove_agency_member_access",
            parameters = RemoveAgencyMemberAccessParams(
                agencyId = agencyId,
                agentId = agentId
            )
        )
        Unit
    }.onFailure {
        HavnLogger.error("Agency", "Failed to remove member $agentId access in $agencyId", it)
    }

    override suspend fun acceptInvite(invitationId: String): Result<Unit> = runCatching {
        client.postgrest.rpc(
            function = "accept_agency_invitation",
            parameters = AgencyInvitationIdParams(invitationId = invitationId)
        )
        Unit
    }.onFailure {
        HavnLogger.error("Agency", "Failed to accept invite $invitationId", it)
    }

    override suspend fun declineInvite(invitationId: String): Result<Unit> = runCatching {
        client.postgrest.rpc(
            function = "decline_agency_invitation",
            parameters = AgencyInvitationIdParams(invitationId = invitationId)
        )
        Unit
    }.onFailure {
        HavnLogger.error("Agency", "Failed to decline invite $invitationId", it)
    }

    private suspend fun memberCount(agencyId: String): Int =
        client.from(AGENCY_MEMBERS)
            .select(Columns.list("agent_id")) {
                filter {
                    eq("agency_id", agencyId)
                    eq("status", "active")
                }
            }
            .decodeList<AgencyMemberIdRow>()
            .size

    private companion object {
        const val USER_ENTITLEMENTS = "user_entitlements"
        const val AGENCIES = "agencies"
        const val AGENCY_MEMBERS = "agency_members"
        const val AGENCY_INVITATIONS = "agency_invitations"
        const val AGENTS = "agents"
        const val WORKSPACES = "workspaces"
        const val MIGRATE_PERSONAL_DATA_TO_AGENCY_RPC = "migrate_owned_personal_data_to_agency"
        val UserEntitlementColumns = Columns.list(
            "tier",
            "can_use_ai_followups",
            "can_use_seller_reports",
            "can_use_advanced_seller_reports",
            "can_use_advanced_analytics",
            "can_create_agency",
            "can_join_agency",
            "max_active_personal_properties",
            "expires_at"
        )
        val AgencyColumns = Columns.list(
            "id",
            "name",
            "icon_url",
            "icon_storage_path",
            "created_by_agent_id",
            "workspace_id"
        )
        val AgencyMemberDetailColumns = Columns.list(
            "agent_id",
            "role",
            "status",
            "created_at"
        )
        val AgentColumns = Columns.list(
            "id",
            "full_name",
            "email",
            "avatar_url",
            "avatar_storage_path"
        )
        val AgencyInvitationColumns = Columns.list(
            "id",
            "email",
            "role",
            "created_at"
        )
    }
}

@Serializable
private data class UserEntitlementRow(
    val tier: String = "free",
    @SerialName("can_use_ai_followups") val canUseAiFollowUps: Boolean = false,
    @SerialName("can_use_seller_reports") val canUseSellerReports: Boolean = false,
    @SerialName("can_use_advanced_seller_reports") val canUseAdvancedSellerReports: Boolean = false,
    @SerialName("can_use_advanced_analytics") val canUseAdvancedAnalytics: Boolean = false,
    @SerialName("can_create_agency") val canCreateAgency: Boolean = false,
    @SerialName("can_join_agency") val canJoinAgency: Boolean = false,
    @SerialName("max_active_personal_properties") val maxActivePersonalProperties: Int = 1,
    @SerialName("expires_at") val expiresAt: String? = null
) {
    fun toDomain(): UserEntitlement = UserEntitlement(
        tier = HavnSubscriptionTier.fromDatabase(tier),
        canUseAiFollowUps = canUseAiFollowUps,
        canUseSellerReports = canUseSellerReports,
        canUseAdvancedSellerReports = canUseAdvancedSellerReports,
        canUseAdvancedAnalytics = canUseAdvancedAnalytics,
        canCreateAgency = canCreateAgency,
        canJoinAgency = canJoinAgency,
        maxActivePersonalProperties = maxActivePersonalProperties,
        expiresAt = expiresAt
    )
}

@Serializable
private data class AgencyRow(
    val id: String,
    val name: String,
    @SerialName("icon_url") val iconUrl: String? = null,
    @SerialName("icon_storage_path") val iconStoragePath: String? = null,
    @SerialName("created_by_agent_id") val createdByAgentId: String,
    @SerialName("workspace_id") val workspaceId: String? = null
) {
    fun toDomain(role: AgencyRole, memberCount: Int): Agency = Agency(
        id = id,
        name = name,
        iconUrl = havnPublicStorageSource(HavnMediaBucket.AgencyMedia, iconStoragePath ?: iconUrl),
        createdByAgentId = createdByAgentId,
        workspaceId = workspaceId,
        role = role,
        memberCount = memberCount
    )
}

@Serializable
private data class AgencyInsertRow(
    val name: String,
    @SerialName("created_by_agent_id") val createdByAgentId: String
)

@Serializable
private data class AgencyBrandingUpdateRow(
    val name: String,
    @SerialName("icon_url") val iconUrl: String? = null,
    @SerialName("icon_storage_path") val iconStoragePath: String? = null
)

@Serializable
private data class AgencyMemberRow(
    @SerialName("agency_id") val agencyId: String,
    val role: String
)

@Serializable
private data class AgencyRoleOnlyRow(
    val role: String
)

@Serializable
private data class AgencyMemberDetailRow(
    @SerialName("agent_id") val agentId: String,
    val role: String,
    val status: String
)

@Serializable
private data class AgencyMemberInsertRow(
    @SerialName("agency_id") val agencyId: String,
    @SerialName("agent_id") val agentId: String,
    val role: String,
    val status: String,
    @SerialName("invited_by_agent_id") val invitedByAgentId: String
)

@Serializable
private data class AgencyMemberIdRow(
    @SerialName("agent_id") val agentId: String
)

@Serializable
private data class AgentRow(
    val id: String,
    @SerialName("full_name") val fullName: String,
    val email: String,
    @SerialName("avatar_url") val avatarUrl: String? = null,
    @SerialName("avatar_storage_path") val avatarStoragePath: String? = null
) {
    val displayAvatarUrl: String?
        get() = havnPublicStorageSource(HavnMediaBucket.AgentPhotos, avatarStoragePath ?: avatarUrl)
}

@Serializable
private data class AgencyInvitationRow(
    val id: String,
    val email: String,
    val role: String
)

@Serializable
private data class PendingAgencyInvitationRow(
    val id: String,
    @SerialName("agency_id") val agencyId: String,
    @SerialName("agency_name") val agencyName: String,
    @SerialName("agency_icon_url") val agencyIconUrl: String? = null,
    val role: String,
    @SerialName("invited_by_name") val invitedByName: String? = null,
    @SerialName("created_at") val createdAt: String = ""
) {
    fun toDomain(): AgencyInvitation = AgencyInvitation(
        id = id,
        agencyId = agencyId,
        agencyName = agencyName,
        agencyIconUrl = agencyIconUrl,
        role = AgencyRole.fromDatabase(role),
        invitedByName = invitedByName,
        createdAt = createdAt
    )
}

@Serializable
private data class InviteExistingAgencyAgentParams(
    @SerialName("p_agency_id") val agencyId: String,
    @SerialName("p_email") val email: String,
    @SerialName("p_role") val role: String
)

@Serializable
private data class DeleteAgencyInvitationParams(
    @SerialName("p_invitation_id") val invitationId: String
)

@Serializable
private data class UpdateAgencyMemberRoleParams(
    @SerialName("p_agency_id") val agencyId: String,
    @SerialName("p_agent_id") val agentId: String,
    @SerialName("p_role") val role: String
)

@Serializable
private data class RemoveAgencyMemberAccessParams(
    @SerialName("p_agency_id") val agencyId: String,
    @SerialName("p_agent_id") val agentId: String
)

@Serializable
private data class AgencyInvitationIdParams(
    @SerialName("p_invitation_id") val invitationId: String
)

@Serializable
private data class WorkspaceInsertRow(
    val type: String,
    @SerialName("agency_id") val agencyId: String,
    val name: String
)

@Serializable
private data class MigratePersonalDataToAgencyParams(
    @SerialName("p_agency_id") val agencyId: String
)

@Serializable
private data class WorkspaceNameUpdateRow(
    val name: String
)

@Serializable
private data class WorkspaceRow(
    val id: String
)

private fun String.sanitizeFileName(): String =
    lowercase()
        .replace(Regex("[^a-z0-9._-]+"), "-")
        .trim('-', '.', '_')
        .ifBlank { "photo.jpg" }

private fun timestampSlug(): String =
    kotlin.time.Clock.System.now().toEpochMilliseconds().toString()

private fun String.withImageExtension(contentType: String): String =
    if (substringAfterLast('/').substringAfterLast('.', "").isNotBlank()) this else "$this.${contentType.havnFileExtension()}"
